import { EmptyProcessCartData, LOTTIE_LOCAL_STORAGE_KEY_PREFIX, MAGIC_ITEMS_LOTTIE_STORAGE_KEY } from "./constants";
import { ReviewedCart } from "../../interfaces/Cart";
import { GlobalHeaderState } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { hasCartSplashLottie } from "@cart-client/helpers/Cart";
import { hasSelectedAddress } from "@cart-client/helpers/CartFooter";
import { SwgyHeaderKeysList } from "@swiggy-private/checkout-core";
import {
    CartHeaderLottieState,
    INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE,
} from "../../contexts/CartHeaderLottieContext";
import {
    IApplyFrequencyCappingLogic,
    IProcessCartHeaderLottieData,
    IUpdateStateForLottieDisplay,
    StartAnimationProps,
} from "./types";
import { updateLottieHeaderState } from "../../helpers/commonCartHeaderLottie";
import { Delay, ThemeConfig } from "@swiggy-private/cart-types";
import { MAGIC_ITEMS_TYPE } from "@swiggy-private/cart-ui";
import { LocalStorage, safeJsonParse } from "@swiggy-private/core-ui";
import { LocalStorageDataForFrequency } from "@swiggy-private/checkout-core";
import { MROReviewedCart } from "@cart-client/interfaces/MROFoodCart";
import { isMROFlow } from "@cart-client/helpers/MROFoodCart";

export const hideTheme = () => {
    return { ...INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE.cartHeaderLottieState, hideTheme: true };
};

/**
 * Check if magic items lottie has played in the current session
 * @param currentSid - Current session ID
 * @returns true if magic items lottie has already played in this session
 */
export const hasMagicItemsLottiePlayedInSession = (currentSid: string): boolean => {
    try {
        const savedData = LocalStorage.getItem(MAGIC_ITEMS_LOTTIE_STORAGE_KEY, {
            counterValue: 0,
            sessionId: "",
        }) as LocalStorageDataForFrequency;

        // If sessionId matches current session, magic items lottie has played
        return !isEmpty(savedData?.sessionId) && savedData.sessionId === currentSid;
    } catch {
        return false;
    }
};

/**
 * Check if magic items are active in the cart
 * @param reviewedCart - The reviewed cart response
 * @returns true if magic items are present and not expired
 */
export const isMagicItemsActive = (
    reviewedCart: ReviewedCart | MROReviewedCart | null,
    isMROCart?: boolean,
): boolean => {
    let crossSellPresentationInfo = reviewedCart?.data?.cross_sell_widget_data?.presentation_info;
    if (isMROCart) {
        // cross sell for MRO only works when there is only one child cart
        const childCarts = (reviewedCart as MROReviewedCart)?.data?.child_carts;
        if (childCarts?.length === 1) {
            crossSellPresentationInfo = childCarts?.[0]?.cross_sell_widget_data?.presentation_info;
        }
    }
    const isMagicItems = crossSellPresentationInfo?.widget_properties_info?.type === MAGIC_ITEMS_TYPE;
    const remainingTime = crossSellPresentationInfo?.timer_data?.remaining_time?.time_in_sec;
    return isMagicItems && remainingTime !== 0;
};

/**
 * Determine if we should skip animation and show final state directly
 * This happens when magic items lottie hasn't played yet in the current session
 * Only for BLACK carts (not BOLT) since BOLT doesn't show cross-sell widget
 */
export const shouldSkipAnimationForMagicItems = (
    reviewedCart: ReviewedCart | null,
    isBolt: boolean,
    isBlck: boolean,
    sid: string,
): boolean => {
    const magicItemsActive = isMagicItemsActive(reviewedCart);
    return !isBolt && isBlck && magicItemsActive && !hasMagicItemsLottiePlayedInSession(sid);
};

export const parseThemeConfig = (themeConfig: string | undefined): ThemeConfig | undefined => {
    return isEmpty(themeConfig) ? undefined : (safeJsonParse<ThemeConfig>(themeConfig as string, {}) as ThemeConfig);
};

export const processCartHeaderLottieData = (cartResponse: ReviewedCart | null): IProcessCartHeaderLottieData => {
    // disable all header lottie animations for MRO cart
    if (isEmpty(cartResponse) || isMROFlow()) {
        return EmptyProcessCartData;
    }
    const isAddressSelected = hasSelectedAddress(cartResponse?.data?.addresses);

    if (!isAddressSelected) {
        return EmptyProcessCartData;
    }

    // Check if magic items are present and not expired
    const crossSellPresentationInfo = cartResponse?.data?.cross_sell_widget_data?.presentation_info;
    const isMagicItems = crossSellPresentationInfo?.widget_properties_info?.type === MAGIC_ITEMS_TYPE;
    const remainingTime = crossSellPresentationInfo?.timer_data?.remaining_time?.time_in_sec;
    const isMagicItemsActive = isMagicItems && remainingTime !== 0;

    // Get current session ID for magic items lottie check
    const currentSid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid) || "";

    // If magic items is active, check if magic items lottie has played in this session
    if (isMagicItemsActive) {
        const hasMagicItemsLottiePlayed = hasMagicItemsLottiePlayedInSession(currentSid);

        if (!hasMagicItemsLottiePlayed) {
            // Magic items lottie has NOT played yet - skip header lottie animation but show final state (gradient)
            const cartHeaderWidget = cartResponse?.data?.cart_header_widget_presentation;
            const cartType = cartHeaderWidget?.cart_type || "";
            const lottieArray = cartHeaderWidget?.header_lottie_config?.lotties || [];
            const hasLottie = !isEmpty(lottieArray);

            // If there's no header lottie config, return empty data
            if (!hasLottie || isEmpty(cartType)) {
                return EmptyProcessCartData;
            }

            const delayData = cartHeaderWidget?.header_config?.delay;
            const frequencyCapData = cartHeaderWidget?.header_config?.frequency_capping;
            const isFrequencyCapEnabled = cartHeaderWidget?.header_config?.enable_frequency_cap === true;
            const rxId = cartResponse?.data?.restaurant_details?.id ?? "";
            const isOtg = cartResponse?.data?.is_swiggy_black_otp_applicable ?? false;
            const sessionFreqCapKey = `${rxId}-${currentSid}`;
            const lottieLocalStorageKey = `${LOTTIE_LOCAL_STORAGE_KEY_PREFIX}_${cartType}`;
            const lottieFrequencyDataObject = JSON.parse(localStorage.getItem(lottieLocalStorageKey) || "{}");
            const themeConfig = cartHeaderWidget?.theme_config;
            const themeConfigObj = parseThemeConfig(themeConfig);

            return {
                cartType,
                lottieArray,
                hasLottie,
                isFrequencyCapEnabled,
                rxId,
                sid: currentSid,
                sessionFreqCapKey,
                lottieLocalStorageKey,
                lottieFrequencyDataObject,
                frequencyCapData,
                delayData,
                isOtg,
                skipAnimationShowFinalState: true, // Skip animation, show final state directly
                themeConfig: themeConfigObj,
            };
        }
        // Magic items lottie HAS played - allow header lottie to play normally (fall through)
    }

    const cartHeaderWidget = cartResponse?.data?.cart_header_widget_presentation;
    const cartType = cartHeaderWidget?.cart_type || "";
    const lottieArray = cartHeaderWidget?.header_lottie_config?.lotties || [];
    const hasLottie = !isEmpty(lottieArray);
    const delayData = cartHeaderWidget?.header_config?.delay;
    const frequencyCapData = cartHeaderWidget?.header_config?.frequency_capping;
    const isFrequencyCapEnabled = cartHeaderWidget?.header_config?.enable_frequency_cap === true;
    const rxId = cartResponse?.data?.restaurant_details?.id ?? "";
    const sid = GlobalHeaderState.getHeader(SwgyHeaderKeysList.sid);
    const isOtg = cartResponse?.data?.is_swiggy_black_otp_applicable ?? false;
    const sessionFreqCapKey = `${rxId}-${sid}`;
    const lottieLocalStorageKey = `${LOTTIE_LOCAL_STORAGE_KEY_PREFIX}_${cartType}`;
    const lottieFrequencyDataObject = JSON.parse(localStorage.getItem(lottieLocalStorageKey) || "{}");
    const themeConfig = cartHeaderWidget?.theme_config;
    const themeConfigObj = parseThemeConfig(themeConfig);

    const shouldSkipAnimation = hasCartSplashLottie(cartResponse) && !isEmpty(cartType);

    return {
        cartType,
        lottieArray,
        hasLottie,
        isFrequencyCapEnabled,
        rxId,
        sid,
        sessionFreqCapKey,
        lottieLocalStorageKey,
        lottieFrequencyDataObject,
        frequencyCapData,
        delayData,
        isOtg,
        themeConfig: themeConfigObj,
        skipAnimationShowFinalState: shouldSkipAnimation,
    };
};

export const applyFrequencyCappingLogic = ({
    isThemeHidden,
    isFrequencyCapEnabled,
    lottieLocalStorageKey,
    lottieFrequencyDataObject,
    sid,
    rxId,
    newCartHeaderState,
    sessionCount,
    count,
    freqCapId,
    hasLottie,
    INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE,
    sessionFreqCapKey,
    sendAnalytics,
}: IApplyFrequencyCappingLogic) => {
    if (isThemeHidden || !hasLottie) {
        const hiddenState = hideTheme();
        return { isThemeHidden: true, updatedState: hiddenState };
    }

    const { finalCartHeaderState: newHeaderState, shouldHideTheme } = updateLottieHeaderState({
        lottieLocalStorageKey,
        lottieFrequencyDataObject,
        sid,
        rxId,
        newCartHeaderState,
        freqCapId,
        count,
        sessionCount,
        sessionFreqCapKey,
        INITIAL_CART_HEADER_LOTTIE_CONTEXT_VALUE,
        sendAnalytics,
        isFrequencyCapEnabled,
    });
    return { isThemeHidden: shouldHideTheme, updatedState: newHeaderState };
};

export const updateStateForLottieDisplay = ({
    newCartHeaderState,
    isThemeHidden,
    hasLottie,
    isSuccess,
    cartType,
    lottieArray,
    themeConfig,
}: IUpdateStateForLottieDisplay) => {
    if (isThemeHidden) {
        return { ...newCartHeaderState, isStatusSuccess: isSuccess };
    }

    // Create object in one go instead of multiple spreads
    return {
        ...newCartHeaderState,
        isStatusSuccess: isSuccess,
        cartType,
        showAnimation: true,
        hideTheme: false,
        hasLottie,
        lottieArray,
        themeConfig,
    };
};

export const isValuesEqual = (value: unknown, other: unknown): boolean => {
    if (value === other) {
        return true;
    }

    if (value == null || other == null || typeof value !== "object" || typeof other !== "object") {
        return false;
    }

    const valueKeys = Object.keys(value as object);
    const otherKeys = Object.keys(other as object);

    if (valueKeys.length !== otherKeys.length) {
        return false;
    }
    for (const key of valueKeys) {
        if (
            !otherKeys.includes(key) ||
            !isValuesEqual((value as Record<string, unknown>)[key], (other as Record<string, unknown>)[key])
        ) {
            return false;
        }
    }

    return true;
};

export const isStateEqual = (newCartHeaderState: CartHeaderLottieState, cartHeaderState: CartHeaderLottieState) => {
    return isValuesEqual(newCartHeaderState, cartHeaderState);
};

const convertDelayToMs = (delayData: Delay): number => {
    if (!delayData) {
        return 0;
    }
    if (delayData?.value && delayData?.value <= 1) {
        return 0;
    }
    switch (delayData?.unit) {
        case "SECONDS":
            return delayData.value * 1000;
        case "MILLI_SECONDS":
            return delayData.value;

        default:
            return delayData.value;
    }
};

export const startAnimation = ({
    newCartHeaderState,
    setCartHeaderLottieState,
    delayData,
    timeoutRef,
}: StartAnimationProps) => {
    if (!(!newCartHeaderState.hideTheme && !newCartHeaderState.showFinalState && !newCartHeaderState.startAnimation)) {
        return;
    }
    if (isEmpty(delayData)) {
        setCartHeaderLottieState((prev: CartHeaderLottieState) => {
            if (!prev.hideTheme) {
                return { ...prev, startAnimation: true };
            }

            return prev;
        });
    } else {
        const delayInMs = convertDelayToMs(delayData);
        const timeoutId = setTimeout(() => {
            setCartHeaderLottieState((prev: CartHeaderLottieState) => {
                if (!prev.hideTheme) {
                    return { ...prev, startAnimation: true };
                }
                return prev;
            });
        }, delayInMs);
        if (timeoutRef) {
            timeoutRef.current = timeoutId;
        }
    }
    return;
};
